import { getScopedTranslate } from '@locale/translate';
import { formatNumber } from '@utils/numbers';

/**
 * What a number field's properties allow. Checked by every control that takes a
 * value for one: the answer, a static default, a manual default mapping.
 */
export type NumberRestrictions = {
  min?: number;
  max?: number;
  disableNegative?: boolean;
  disableDecimal?: boolean;
};

export type NumberRestrictionViolation = 'decimal' | 'negative' | 'min' | 'max';

export const getNumberRestrictionViolation = (
  value: number | null | undefined,
  restrictions: NumberRestrictions
): NumberRestrictionViolation | undefined => {
  if (value == null || !Number.isFinite(value)) return undefined;
  if (restrictions.disableDecimal && !Number.isInteger(value)) return 'decimal';
  if (restrictions.disableNegative && value < 0) return 'negative';
  if (restrictions.min != null && value < restrictions.min) return 'min';
  if (restrictions.max != null && value > restrictions.max) return 'max';
  return undefined;
};

/** The message for the violation, if there is one. */
export const getNumberRestrictionError = (
  value: number | null | undefined,
  restrictions: NumberRestrictions
): string | undefined => {
  const { min, max } = restrictions;
  switch (getNumberRestrictionViolation(value, restrictions)) {
    case 'decimal':
      return t('errorNoDecimal');
    case 'negative':
      return t('errorNoNegative');
    case 'min':
      return min == null ? undefined : t('errorMin', { min: formatNumber(min) });
    case 'max':
      return max == null ? undefined : t('errorMax', { max: formatNumber(max) });
    default:
      return undefined;
  }
};

const { t } = getScopedTranslate({
  en: {
    errorNoNegative: 'Negative numbers not allowed',
    errorNoDecimal: 'Decimal numbers not allowed',
    errorMin: 'Minimum is {min}',
    errorMax: 'Maximum is {max}',
  },
});
